/**
 * Trodo design tokens — single source of truth for ALL colors used in the app.
 *
 * Architecture:
 *   1. BRAND tokens (orange) — identical in light and dark mode.
 *   2. SEMANTIC tokens (surface / border / text / state / feedback) — have a
 *      light value and a dark value. Consumers reference them by ROLE, never
 *      by hex.
 *   3. `applyTrodoPrimaryCssVars()` writes BOTH palettes onto `:root` (light)
 *      and `:root[data-theme="dark"]` (dark) as CSS custom properties.
 *      Flipping `data-theme` on `<html>` instantly switches the whole app.
 *
 * Consumer rules:
 *   - JS/JSX: import a named token (e.g. `surface.base`) or use the CSS var
 *     directly (`var(--surface-base)`).
 *   - Tailwind: prefer the semantic class names defined in tailwind.config.js
 *     (e.g. `bg-surface`, `text-primary`, `border-default`). They resolve to
 *     CSS vars so they flip automatically.
 *   - CSS: use `var(--token-name)`.
 *   - NEVER write a raw hex outside this file. New colors must be added here.
 *
 * Server-side HTML mirrors PRIMARY_HEX in `backend/config/trodoBrand.js`.
 */

// =============================================================================
// BRAND — identical in both modes
// =============================================================================
export const PRIMARY_HEX = "#ff6d29";
export const primary = PRIMARY_HEX;
export const primaryLight = PRIMARY_HEX;
export const primaryHover = "#e55f1f";
export const PRIMARY_RGB = "255 109 41";

// =============================================================================
// LEGACY ink/grey exports (kept for back-compat with existing imports).
// New code should use `text.primary`, `text.secondary`, etc.
// =============================================================================
export const ink = "#0f172a";
export const inkDeep = "#020617";
export const INK_RGB = "15 23 42";
export const INK_DEEP_RGB = "2 6 23";
export const grey = "#475569";
export const greyLight = "#64748b";
export const GREY_RGB = "71 85 105";
export const GREY_LIGHT_RGB = "100 116 139";

// =============================================================================
// SEMANTIC PALETTE
//
// Each token has { light, dark }. Add new roles here as needed — never inline
// a hex in a component.
// =============================================================================
export const palette = {
  // ---- Surfaces (backgrounds, in order from deepest to most raised) ----
  // Dark values are intentionally NEUTRAL (no blue tint) and a touch deeper so
  // the UI reads matte/muted rather than shiny.
  surface: {
    sunken:   { light: "#f5f5f5", dark: "#0a0a0b" }, // sidebar rail, page bg behind cards
    base:     { light: "#ffffff", dark: "#141415" }, // default page / card background
    raised:   { light: "#ffffff", dark: "#1b1b1d" }, // popovers, dropdowns, modals
    overlay:  { light: "rgba(0,0,0,0.45)", dark: "rgba(0,0,0,0.65)" }, // modal scrims
    hover:    { light: "#e8eaed", dark: "#232325" }, // row/button hover background
    selected: { light: "#fef3ec", dark: "#3a2418" }, // selected-state tint (orange-derived)
    inverse:  { light: "#0f172a", dark: "#f5f5f5" },
  },

  // ---- Borders & dividers ----
  border: {
    subtle:   { light: "#eef0f4", dark: "#202022" }, // hairline dividers
    default:  { light: "#DDE2EE", dark: "#2a2a2d" }, // standard card/input borders
    strong:   { light: "#c5cbda", dark: "#3a3a3e" }, // emphasized borders
    focus:    { light: "#ff6d29", dark: "#ff6d29" }, // focus ring (brand)
  },

  // ---- Text ----
  // Per design rule: ONE canonical grey for all "muted/secondary" copy in each
  // mode. `secondary` and `muted` resolve to the same value so tables / labels
  // / hints all share a single tone. Adjust ONLY here.
  //   Light: gray-500-ish (#6b7280) — lighter than the old slate-600 for
  //          better readability without dropping contrast below WCAG AA on
  //          white surfaces (4.83:1).
  //   Dark:  near-white grey (#c8ccd4) — bright enough to read on dark
  //          surfaces without competing with the primary text color.
  text: {
    primary:   { light: "#0f172a", dark: "#f1f3f6" }, // body & UI text
    secondary: { light: "#6b7280", dark: "#c8ccd4" }, // ONE canonical grey
    muted:     { light: "#6b7280", dark: "#c8ccd4" }, // same as secondary
    disabled:  { light: "#a3a8b2", dark: "#7d838e" }, // dimmer, still readable
    inverse:   { light: "#ffffff", dark: "#0f172a" }, // text on inverse/brand surfaces
    onBrand:   { light: "#ffffff", dark: "#ffffff" }, // text on orange backgrounds
    deep:      { light: "#020617", dark: "#ffffff" }, // hero/max-contrast titles
  },

  // ---- Interactive states ----
  state: {
    hoverBg:    { light: "rgba(0,0,0,0.04)", dark: "rgba(255,255,255,0.06)" },
    activeBg:   { light: "rgba(0,0,0,0.08)", dark: "rgba(255,255,255,0.10)" },
    selectedBg: { light: "rgba(255,109,41,0.10)", dark: "rgba(255,109,41,0.18)" },
    focusRing:  { light: "rgba(255,109,41,0.35)", dark: "rgba(255,109,41,0.45)" },
  },

  // ---- Feedback colors ----
  feedback: {
    success:    { light: "#16a34a", dark: "#22c55e" },
    successBg:  { light: "#ecfdf5", dark: "#0f2a1c" },
    warning:    { light: "#d97706", dark: "#f59e0b" },
    warningBg:  { light: "#fffbeb", dark: "#2a1f0a" },
    error:      { light: "#dc2626", dark: "#ef4444" },
    errorBg:    { light: "#fef2f2", dark: "#2a1414" },
    info:       { light: "#2563eb", dark: "#3b82f6" },
    infoBg:     { light: "#eff6ff", dark: "#0f1f3a" },
  },

  // ---- Accents ----
  // Decorative labels that are neither brand nor feedback. Unlike brand these
  // DO need a dark variant: the light teal below is ~2.6:1 on a dark surface.
  accent: {
    enterprise: { light: "#0d9488", dark: "#2dd4bf" },
    // Span kind "LLM". The other span kinds reuse feedback colours (tool =
    // warning, retrieval = success, agent = info); a model call is none of
    // those, so it gets the one hue the palette was missing. The light violet
    // is ~3.1:1 on a dark surface, hence the separate dark variant.
    llm:        { light: "#7c3aed", dark: "#a78bfa" },
  },

  // ---- Brand (identical, but exposed here for completeness) ----
  brand: {
    primary:      { light: PRIMARY_HEX,   dark: PRIMARY_HEX },
    primaryHover: { light: primaryHover,  dark: primaryHover },
    primarySoft:  { light: "#fff2ec",     dark: "#3a2418" }, // light tint of orange
  },

  // ---- Shadows ----
  shadow: {
    sm: { light: "0 1px 2px rgba(0,0,0,0.05)",  dark: "0 1px 2px rgba(0,0,0,0.4)" },
    md: { light: "0 2px 8px rgba(0,0,0,0.05)",  dark: "0 2px 8px rgba(0,0,0,0.5)" },
    lg: { light: "0 4px 16px rgba(0,0,0,0.10)", dark: "0 4px 16px rgba(0,0,0,0.6)" },
  },

  // ---- Scrollbar ----
  scrollbar: {
    thumb:      { light: "rgba(0,0,0,0.15)",  dark: "rgba(255,255,255,0.18)" },
    thumbHover: { light: "rgba(0,0,0,0.22)",  dark: "rgba(255,255,255,0.28)" },
  },
};

// =============================================================================
// Flat JS accessors — `surface.base.light`, etc. Convenient for inline styles
// that need to pick a mode explicitly. Prefer CSS vars for theme-aware code.
// =============================================================================
export const surface = palette.surface;
export const border = palette.border;
export const text = palette.text;
export const state = palette.state;
export const feedback = palette.feedback;
export const brand = palette.brand;
export const shadow = palette.shadow;
export const scrollbar = palette.scrollbar;

/** @param {number} alpha 0–1 */
export function primaryAlpha(alpha) {
  return `rgba(${PRIMARY_RGB.replace(/\s+/g, ", ")}, ${alpha})`;
}

// =============================================================================
// CSS variable emission
// =============================================================================

/**
 * Flatten the palette into a list of [cssVar, light, dark] triples.
 * `palette.surface.base` -> `--surface-base`
 */
function flattenPalette() {
  const out = [];
  for (const [group, tokens] of Object.entries(palette)) {
    for (const [name, value] of Object.entries(tokens)) {
      const cssVar = `--${group}-${name.replace(/([A-Z])/g, "-$1").toLowerCase()}`;
      out.push([cssVar, value.light, value.dark]);
    }
  }
  return out;
}

/** "#141415" -> "20 20 21". Returns null for anything that isn't a hex colour. */
function hexToRgbChannels(value) {
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(String(value).trim());
  if (!m) return null;
  let hex = m[1];
  if (hex.length === 3) hex = hex.split("").map((c) => c + c).join("");
  const n = parseInt(hex, 16);
  return `${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255}`;
}

/**
 * Space-separated channel twins for every hex token: `--surface-base-rgb`.
 *
 * Tailwind can only compute an alpha channel when it knows the underlying
 * channels. A bare `var(--surface-hover)` is opaque to it, so `bg-surface-hover/80`
 * was silently DROPPED from the build — the element fell back to Tailwind's
 * default colour (or none at all). tailwind.config.js maps tokens through
 * `rgb(var(--x-rgb) / <alpha-value>)` so the modifier works.
 *
 * Only hex tokens get a twin — `surface.overlay`, the state tints, shadows and
 * scrollbar values are already rgba()/composite and keep using the plain var.
 */
function flattenChannels() {
  return flattenPalette()
    .map(([cssVar, light, dark]) => {
      const l = hexToRgbChannels(light);
      const d = hexToRgbChannels(dark);
      return l && d ? [`${cssVar}-rgb`, l, d] : null;
    })
    .filter(Boolean);
}

/**
 * Inject one <style> tag with both light and dark variable sets, plus the
 * legacy --trodo-* vars used throughout the existing codebase.
 *
 * Call once from `index.js` before render.
 */
export function applyTrodoPrimaryCssVars() {
  if (typeof document === "undefined") return;

  // Colour vars + their -rgb channel twins (see flattenChannels).
  const triples = [...flattenPalette(), ...flattenChannels()];
  const lightDecls = triples.map(([v, l]) => `  ${v}: ${l};`).join("\n");
  const darkDecls  = triples.map(([v, , d]) => `  ${v}: ${d};`).join("\n");

  // Legacy --trodo-* vars (kept verbatim so nothing in the codebase breaks).
  const legacyLight = `
  --trodo-primary: ${PRIMARY_HEX};
  --trodo-primary-rgb: ${PRIMARY_RGB};
  --trodo-primary-hover: ${primaryHover};
  --trodo-ink: ${ink};
  --trodo-ink-rgb: ${INK_RGB};
  --trodo-ink-deep: ${inkDeep};
  --trodo-ink-deep-rgb: ${INK_DEEP_RGB};
  --trodo-grey: ${grey};
  --trodo-grey-rgb: ${GREY_RGB};
  --trodo-grey-light: ${greyLight};
  --trodo-grey-light-rgb: ${GREY_LIGHT_RGB};`;
  // In dark mode, --trodo-ink/grey should follow text.primary / text.secondary
  // so legacy consumers flip automatically.
  const legacyDark = `
  --trodo-ink: ${palette.text.primary.dark};
  --trodo-ink-deep: ${palette.text.deep.dark};
  --trodo-grey: ${palette.text.secondary.dark};
  --trodo-grey-light: ${palette.text.muted.dark};`;

  const css = `
:root {
${lightDecls}
${legacyLight}
}
:root[data-theme="dark"] {
${darkDecls}
${legacyDark}
}
`;

  let tag = document.getElementById("trodo-theme-vars");
  if (!tag) {
    tag = document.createElement("style");
    tag.id = "trodo-theme-vars";
    document.head.appendChild(tag);
  }
  tag.textContent = css;
}
